/**
 * Equipment Status Utilities
 * Determines service status of equipment based on service history
 * Used for badge calculations, visual indicators, and auto-selection
 */

import { serviceTypeFromJob } from '../services/maintenance/serviceType';

/**
 * Get equipment service status based on service history and job interval
 * @param {Object} equipment - Equipment item from register
 * @param {string|Object} jobIdOrJob - Maintenance job ID or job object
 * @param {number} intervalMonths - Service interval in months
 * @param {number} warningDays - Days before due to show "due soon" (default 30)
 * @returns {Object} { status: 'overdue'|'dueSoon'|'scheduled'|'never', daysUntilDue: number, lastService: string|null, nextDue: string|null }
 */
export function getEquipmentStatus(equipment, jobIdOrJob, intervalMonths, warningDays = 30) {
  // Get normalized service type from job
  const serviceType = typeof jobIdOrJob === 'object'
    ? serviceTypeFromJob(jobIdOrJob)
    : serviceTypeFromJob({ id: jobIdOrJob });

  // Check if equipment has service history
  if (!equipment.serviceHistory || !Array.isArray(equipment.serviceHistory) || equipment.serviceHistory.length === 0) {
    return {
      status: 'never',
      daysUntilDue: null,
      lastService: null,
      nextDue: null
    };
  }

  // Find most recent service for this specific service type
  const relevantServices = equipment.serviceHistory.filter(service =>
    service.serviceType === serviceType ||
    // Fallback to jobId comparison for backwards compatibility
    service.jobId === jobIdOrJob ||
    (typeof jobIdOrJob === 'string' && service.type?.toLowerCase().includes(jobIdOrJob.split('.').pop()))
  );

  if (relevantServices.length === 0) {
    return {
      status: 'never',
      daysUntilDue: null,
      lastService: null,
      nextDue: null
    };
  }

  // Sort by date descending to get most recent
  const sortedServices = relevantServices.sort((a, b) =>
    new Date(b.date) - new Date(a.date)
  );

  const lastService = sortedServices[0];
  const lastServiceDate = new Date(lastService.date);

  // Calculate next due date
  const nextDueDate = new Date(lastServiceDate);
  nextDueDate.setMonth(nextDueDate.getMonth() + intervalMonths);

  // Calculate days until due
  const today = new Date();
  const daysUntilDue = Math.floor((nextDueDate - today) / (1000 * 60 * 60 * 24));

  // Determine status
  let status;
  if (daysUntilDue < 0) {
    status = 'overdue';
  } else if (daysUntilDue <= warningDays) {
    status = 'dueSoon';
  } else {
    status = 'scheduled';
  }

  return {
    status,
    daysUntilDue,
    lastService: lastService.date,
    nextDue: nextDueDate.toISOString().split('T')[0]
  };
}

/**
 * Get status summary for multiple equipment items
 * @param {Array} equipmentList - Array of equipment items
 * @param {string} jobId - Maintenance job ID
 * @param {number} intervalMonths - Service interval in months
 * @param {number} warningDays - Days before due to show "due soon"
 * @returns {Object} { overdue: number, dueSoon: number, scheduled: number, never: number, items: Array }
 */
export function getEquipmentStatusSummary(equipmentList, jobId, intervalMonths, warningDays = 30) {
  const summary = {
    overdue: 0,
    dueSoon: 0,
    scheduled: 0,
    never: 0,
    items: []
  };

  equipmentList.forEach(equipment => {
    const status = getEquipmentStatus(equipment, jobId, intervalMonths, warningDays);

    summary[status.status]++;
    summary.items.push({
      ...equipment,
      statusInfo: status
    });
  });

  return summary;
}

/**
 * Get color class for status display
 * @param {string} status - Status from getEquipmentStatus
 * @returns {string} Tailwind color class
 */
export function getStatusColorClass(status) {
  const colorMap = {
    overdue: 'text-red-600 bg-red-50 border-red-200',
    dueSoon: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    scheduled: 'text-green-600 bg-green-50 border-green-200',
    never: 'text-gray-600 bg-gray-50 border-gray-200'
  };

  return colorMap[status] || colorMap.scheduled;
}

/**
 * Get status badge color (for simpler displays)
 * @param {string} status - Status from getEquipmentStatus
 * @returns {string} Color name
 */
export function getStatusBadgeColor(status) {
  const colorMap = {
    overdue: 'red',
    dueSoon: 'yellow',
    scheduled: 'green',
    never: 'gray'
  };

  return colorMap[status] || 'gray';
}

/**
 * Format days until due for display
 * @param {number} daysUntilDue - Days until due (negative if overdue)
 * @returns {string} Formatted string
 */
export function formatDaysUntilDue(daysUntilDue) {
  if (daysUntilDue === null) return 'Never serviced';
  if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)} days overdue`;
  if (daysUntilDue === 0) return 'Due today';
  if (daysUntilDue === 1) return 'Due tomorrow';
  return `Due in ${daysUntilDue} days`;
}

/**
 * Sort equipment by priority (overdue first, then due soon, then scheduled)
 * @param {Array} equipmentList - Array with statusInfo added
 * @returns {Array} Sorted array
 */
export function sortByPriority(equipmentList) {
  const priorityOrder = {
    overdue: 0,
    dueSoon: 1,
    scheduled: 2,
    never: 3
  };

  return [...equipmentList].sort((a, b) => {
    const aPriority = priorityOrder[a.statusInfo?.status] ?? 999;
    const bPriority = priorityOrder[b.statusInfo?.status] ?? 999;

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If same priority, sort by days until due (most urgent first)
    if (a.statusInfo?.daysUntilDue !== null && b.statusInfo?.daysUntilDue !== null) {
      return a.statusInfo.daysUntilDue - b.statusInfo.daysUntilDue;
    }

    return 0;
  });
}

/**
 * Calculate maintenance job badge from linked equipment status
 * @param {Object} job - Maintenance job object
 * @param {Array} equipmentList - All equipment items
 * @returns {Object} { itemsTotal: number, itemsDue: number, itemsOverdue: number, nextDue: string|null }
 */
export function calculateJobBadge(job, equipmentList) {
  if (!job.linkedEquipmentIds || job.linkedEquipmentIds.length === 0) {
    return {
      itemsTotal: 0,
      itemsDue: 0,
      itemsOverdue: 0,
      nextDue: null
    };
  }

  // Get linked equipment
  const linkedEquipment = equipmentList.filter(eq =>
    job.linkedEquipmentIds.includes(eq.id)
  );

  // Get status for each
  const summary = getEquipmentStatusSummary(
    linkedEquipment,
    job.id,
    job.interval || 12,
    30 // warning days
  );

  // Items "due" = overdue + dueSoon + never serviced
  const itemsDue = summary.overdue + summary.dueSoon + summary.never;

  // Find next due date
  let nextDue = null;
  const scheduledItems = summary.items.filter(item =>
    item.statusInfo.status === 'scheduled' || item.statusInfo.status === 'dueSoon'
  );

  if (scheduledItems.length > 0) {
    // Sort by nextDue date to find earliest
    scheduledItems.sort((a, b) =>
      new Date(a.statusInfo.nextDue) - new Date(b.statusInfo.nextDue)
    );
    nextDue = scheduledItems[0].statusInfo.nextDue;
  }

  return {
    itemsTotal: linkedEquipment.length,
    itemsDue,
    itemsOverdue: summary.overdue,
    nextDue
  };
}
